import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { adminApi, api, formatApiError } from "@/lib/api";
import { ClientInfoSection, CLIENT_TYPE_LABELS, isProClient } from "./ClientInfoSection";
import { EventCountdown, AnimationsPanel, VehiclesPanel, TimelinePanel } from "./ClientCrmPanels";
import { NewRentalDialog } from "@/components/location/NewRentalDialog";
import { DatePickerField, DateTimeField } from "@/components/location/DateTimeFields";
import { Boxes } from "lucide-react";
import { ArrowLeft, Plus, Trash2, FileText, FilePlus, Pencil, Save, X, AlertTriangle, CheckCircle2, Circle, Eye, Send, Key, RefreshCw, Copy, ListMusic, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";

function fmtEur(v) {
  const n = Number(v || 0);
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR");
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
const DOC_LABEL = {
  devis: "Devis",
  facture: "Facture",
  facture_acompte: "Facture acompte",
};

const ORIGINE_OPTIONS = [
  { value: "site_internet", label: "Site internet" },
  { value: "recommandation", label: "Recommandation" },
  { value: "mariage_net", label: "Mariage.net" },
  { value: "bark", label: "Bark" },
  { value: "evenementiel_pour_tous", label: "Événementiel pour Tous" },
  { value: "autre", label: "Autre à préciser" },
];
const ORIGINE_LABELS = ORIGINE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

function Section({ title, children, testid, action }) {
  return (
    <div className="card-luxe p-6 mb-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="font-serif-luxe text-xl">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/45">{k}</p>
      <p className="text-sm mt-1 break-words">{v || <span className="text-white/30 italic">—</span>}</p>
    </div>
  );
}

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  // RDV form
  const [rdvForm, setRdvForm] = useState({ date: "", lieu: "", type: "Rendez-vous préparation", notes: "" });
  // Contrat form
  const [contratForm, setContratForm] = useState({ numero: "", montant: "", statut: "brouillon", date_signature: "", notes: "", url_pdf: "", assurance_url: "" });

  // Coordinates edit mode

  // Origine du dossier edit mode
  const [editingOrigine, setEditingOrigine] = useState(false);
  const [origineForm, setOrigineForm] = useState({ origine: "", origine_autre: "" });
  const [savingOrigine, setSavingOrigine] = useState(false);

  // Credentials (identifiants & mot de passe)
  const [resetPwdBusy, setResetPwdBusy] = useState(false);
  const [rentalOpen, setRentalOpen] = useState(false);
  const [tempPwd, setTempPwd] = useState("");  // shown once after reset
  const [resetPlaylistBusy, setResetPlaylistBusy] = useState(false);

  const load = () => adminApi.userFull(id).then((d) => {
    setData(d);
    if (d.contrat) setContratForm({ ...contratForm, ...d.contrat });
  }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  if (err) return <p className="text-red-300">{err}</p>;
  if (!data) return <p className="text-white/55 text-sm">Chargement…</p>;

  const u = data.user;

  const addRdv = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(rdvForm.date)) { toast.error("Choisissez la date et l'heure du rendez-vous."); return; }
    try {
      await adminApi.createRdv({ user_id: id, ...rdvForm });
      setRdvForm({ date: "", lieu: "", type: "Rendez-vous préparation", notes: "" });
      load();
    } catch (e2) { alert(formatApiError(e2)); }
  };
  const delRdv = async (rid) => {
    if (!window.confirm("Supprimer ce RDV ?")) return;
    await adminApi.deleteRdv(rid); load();
  };
  const saveContrat = async (e) => {
    e.preventDefault();
    try { await adminApi.upsertContrat({ user_id: id, ...contratForm }); load(); alert("Contrat enregistré."); }
    catch (e2) { alert(formatApiError(e2)); }
  };

  return (
    <div data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457] mb-6">
        <ArrowLeft size={13} strokeWidth={1.4} /> Retour
      </Link>
      <header className="mb-8">
        <div className="flex items-baseline gap-3 flex-wrap">
          <p className="eyebrow">Fiche client</p>
          {u.client_number && (
            <span
              data-testid="client-mya-badge"
              className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/50 px-2 py-1 font-mono"
            >
              {u.client_number}
            </span>
          )}
        </div>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-2">{u.name}</h1>
        <div className="flex items-center gap-3 flex-wrap mt-2">
          <span data-testid="client-type-badge" className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${isProClient(u) ? "text-[#DCB457] border-[#DCB457]/50" : "text-white/60 border-white/20"}`}>
            {CLIENT_TYPE_LABELS[u.client_type] || "Particulier"}
          </span>
          {u.raison_sociale && <span className="text-sm text-[#DCB457]" data-testid="client-company">{u.raison_sociale}</span>}
          <p className="text-white/55 text-sm">{u.email} · {u.phone || "—"} · {u.type_evenement || "type non précisé"}</p>
          <button type="button" onClick={() => setRentalOpen(true)} className="btn-ghost-gold !py-2 !text-[10px] inline-flex gap-1.5 mt-3" data-testid="client-new-rental"><Boxes size={12} />Créer une location</button>
        </div>
      </header>
      {rentalOpen && <NewRentalDialog onClose={() => setRentalOpen(false)} client={{
        user_id: id, email: u.email, phone: u.phone,
        prenom: u.prenom || (u.name || "").split(" ")[0], nom: u.nom || (u.name || "").split(" ").slice(1).join(" "),
        address: [u.adresse, u.adresse_complement, [u.code_postal, u.ville].filter(Boolean).join(" ")].filter(Boolean).join(", "),
      }} />}

      <EventCountdown userId={id} />
      <ClientInfoSection u={u} id={id} onSaved={load} />
      <div className="grid gap-6 mb-6">
        <AnimationsPanel userId={id} />
        <VehiclesPanel userId={id} />
        <TimelinePanel userId={id} />
      </div>

      {/* Warnings banner */}
      {data.dossier && data.dossier.warnings_count > 0 && (
        <div
          className="mb-6 flex items-start gap-3 border border-amber-400/40 bg-amber-400/8 px-5 py-4"
          data-testid="dossier-warning-banner"
        >
          <AlertTriangle size={18} strokeWidth={1.5} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-amber-200 font-medium">
              {data.dossier.warnings_count} alerte{data.dossier.warnings_count > 1 ? "s" : ""} sur ce dossier
            </p>
            <p className="text-amber-100/70 text-xs mt-1">
              Certaines étapes stagnent — consultez le suivi ci-dessous pour identifier les actions à mener.
            </p>
          </div>
        </div>
      )}

      {/* Financial rollup */}
      {data.dossier && data.dossier.finance && data.dossier.finance.total_ttc > 0 && (
        <div className="mb-6 card-luxe p-5" data-testid="dossier-finance">
          <div className="flex items-center justify-between mb-3">
            <p className="eyebrow">Situation financière du dossier</p>
            {data.dossier.finance.is_settled && (
              <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-400/40 px-2 py-0.5">
                Soldé
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Total TTC</div>
              <div className="text-white/90 font-medium mt-1" data-testid="finance-total">{fmtEur(data.dossier.finance.total_ttc)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Encaissé</div>
              <div className="text-emerald-300 font-medium mt-1" data-testid="finance-paid">{fmtEur(data.dossier.finance.paid)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Reste à encaisser</div>
              <div
                className={`font-medium mt-1 ${data.dossier.finance.remaining === 0 ? "text-emerald-300" : "text-[#DCB457]"}`}
                data-testid="finance-remaining"
              >
                {fmtEur(data.dossier.finance.remaining)}
              </div>
            </div>
          </div>
          {data.dossier.finance.total_ttc > 0 && (
            <div className="mt-4">
              <div className="h-1.5 bg-white/8 overflow-hidden rounded-full">
                <div
                  className="h-full bg-emerald-400/70 transition-all"
                  style={{ width: `${Math.min(100, Math.round((data.dossier.finance.paid / data.dossier.finance.total_ttc) * 100))}%` }}
                />
              </div>
              <div className="mt-1 text-[10px] text-white/45 text-right">
                {Math.round((data.dossier.finance.paid / data.dossier.finance.total_ttc) * 100)}% encaissé
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dossier timeline */}
      {data.dossier && data.dossier.steps && data.dossier.steps.length > 0 && (
        <Section title="Suivi du dossier" testid="sec-dossier-timeline">
          {data.dossier.event_date && (
            <p className="text-xs text-white/50 mb-4">
              Événement prévu le <strong className="text-[#DCB457]">{fmtDate(data.dossier.event_date)}</strong>
              {data.dossier.days_to_event !== null && data.dossier.days_to_event >= 0 && (
                <> · dans {data.dossier.days_to_event} jour{data.dossier.days_to_event > 1 ? "s" : ""}</>
              )}
              {data.dossier.days_to_event !== null && data.dossier.days_to_event < 0 && (
                <> · passé il y a {Math.abs(data.dossier.days_to_event)} jour{Math.abs(data.dossier.days_to_event) > 1 ? "s" : ""}</>
              )}
              {data.dossier.primary_devis_number && <> · Devis {data.dossier.primary_devis_number}</>}
            </p>
          )}
          <ol className="space-y-2.5">
            {data.dossier.steps.map((s) => (
              <li
                key={s.key}
                data-testid={`dossier-step-${s.key}`}
                className={`flex items-start gap-3 py-1.5 pl-1 border-l-2 transition-colors ${
                  s.warning ? "border-amber-400" : s.done ? "border-[#DCB457]" : "border-white/12"
                }`}
              >
                <div className="mt-0.5 shrink-0">
                  {s.warning ? (
                    <AlertTriangle size={14} strokeWidth={1.6} className="text-amber-300" />
                  ) : s.done ? (
                    <CheckCircle2 size={14} strokeWidth={1.6} className="text-[#DCB457]" />
                  ) : (
                    <Circle size={14} strokeWidth={1.4} className="text-white/30" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className={`text-sm ${s.done ? "text-white/90" : s.warning ? "text-amber-200" : "text-white/50"}`}>
                      {s.label}
                    </span>
                    {s.date && <span className="text-[11px] text-white/45 shrink-0">{fmtDate(s.date)}</span>}
                  </div>
                  {s.warning && s.reason && (
                    <p className="text-[11px] text-amber-200/85 mt-0.5">{s.reason}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Devis & Factures */}
      <Section title="Devis & Factures" testid="sec-billing-docs">
        {data.billing_docs && data.billing_docs.length > 0 ? (
          <div className="space-y-2">
            {data.billing_docs.map((d) => (
              <div
                key={d.id}
                data-testid={`client-billing-${d.number}`}
                className="flex items-start gap-3 border border-[#DCB457]/15 hover:border-[#DCB457]/40 transition-colors px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <Link to={`/admin/devis/${d.id}`} className="font-mono text-xs text-[#DCB457] hover:underline">
                      {d.number}
                    </Link>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-white/40">
                      {DOC_LABEL[d.doc_type] || d.doc_type}
                    </span>
                    {d.status && (
                      <span className="text-[10px] uppercase tracking-[0.16em] text-[#DCB457]/85 border border-[#DCB457]/25 px-1.5 py-0.5">
                        {d.status.replace("_", " ")}
                      </span>
                    )}
                    {d.is_signed && (
                      <span className="text-[10px] text-emerald-300 flex items-center gap-1">
                        <CheckCircle2 size={10} strokeWidth={1.5} /> Signé
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {d.event_type} {d.event_date && `· ${fmtDate(d.event_date)}`}
                    {d.event_location && ` · ${d.event_location}`}
                  </div>
                  <div className="text-[11px] text-white/45 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>Créé {fmtDate(d.created_at)}</span>
                    {d.sent_at && <span>Envoyé {fmtDate(d.sent_at)}</span>}
                    {d.viewed_at && <span>Consulté {fmtDate(d.viewed_at)}</span>}
                    {d.signed_at && <span className="text-[#DCB457]">Signé {fmtDate(d.signed_at)}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[#DCB457] font-medium text-sm">{fmtEur(d.total_ttc)}</div>
                  <Link
                    to={`/admin/devis/${d.id}`}
                    className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-[#DCB457] mt-1"
                  >
                    <Pencil size={10} strokeWidth={1.5} /> Ouvrir
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/50 text-sm italic">Aucun devis ni facture pour ce client.</p>
        )}
      </Section>

      <PrestatairesSection userId={id} assigned={data.prestataires_assigned || []} onChange={load} />

      <MyEventSection userId={id} />

      <Section
        title="Origine du dossier"
        testid="sec-origine"
        action={
          !editingOrigine ? (
            <button
              type="button"
              onClick={() => {
                setOrigineForm({
                  origine: u.origine || "",
                  origine_autre: u.origine_autre || "",
                });
                setEditingOrigine(true);
              }}
              data-testid="btn-edit-origine"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 transition-colors"
            >
              <Pencil size={12} strokeWidth={1.5} /> Modifier
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  setSavingOrigine(true);
                  try {
                    const payload = { origine: origineForm.origine || "" };
                    payload.origine_autre = origineForm.origine === "autre" ? (origineForm.origine_autre || "") : "";
                    await adminApi.updateUser(id, payload);
                    toast.success("Origine du dossier mise à jour.");
                    setEditingOrigine(false);
                    load();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally {
                    setSavingOrigine(false);
                  }
                }}
                disabled={savingOrigine}
                data-testid="btn-save-origine"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 disabled:opacity-60"
              >
                <Save size={12} strokeWidth={1.7} /> {savingOrigine ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button
                type="button"
                onClick={() => setEditingOrigine(false)}
                disabled={savingOrigine}
                data-testid="btn-cancel-origine"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-white/20 text-white/65 hover:bg-white/5"
              >
                <X size={12} strokeWidth={1.5} /> Annuler
              </button>
            </div>
          )
        }
      >
        {!editingOrigine ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <KV k="Origine" v={ORIGINE_LABELS[u.origine] || (u.origine ? u.origine : "")} />
            {u.origine === "autre" && <KV k="Précision" v={u.origine_autre} />}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Origine</label>
              <select
                value={origineForm.origine}
                onChange={(e) => setOrigineForm({ ...origineForm, origine: e.target.value })}
                className="input-luxe mt-1 w-full bg-black/60"
                data-testid="select-origine"
              >
                <option value="" className="bg-black">— Non renseigné —</option>
                {ORIGINE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-black">{o.label}</option>
                ))}
              </select>
            </div>
            {origineForm.origine === "autre" && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Précisez</label>
                <input
                  type="text"
                  value={origineForm.origine_autre}
                  onChange={(e) => setOrigineForm({ ...origineForm, origine_autre: e.target.value })}
                  className="input-luxe mt-1 w-full"
                  placeholder="Ex: bouche-à-oreille salon"
                  data-testid="input-origine-autre"
                />
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ===== Identifiants & accès ===== */}
      <Section title="Identifiants & accès espace client" testid="sec-credentials">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1.5">Identifiant (email)</p>
            <div className="flex items-center gap-2">
              <code
                data-testid="client-login-email"
                className="flex-1 bg-black/40 border border-[#DCB457]/25 px-3 py-2 text-sm text-white font-mono truncate"
              >
                {u.email}
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(u.email);
                  toast.success("Email copié.");
                }}
                data-testid="btn-copy-email"
                title="Copier l'email"
                className="p-2 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10"
              >
                <Copy size={13} strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1.5">Mot de passe</p>
            {tempPwd ? (
              <div className="flex items-center gap-2">
                <code
                  data-testid="client-temp-password"
                  className="flex-1 bg-[#DCB457]/10 border border-[#DCB457]/60 px-3 py-2 text-sm text-[#DCB457] font-mono truncate"
                >
                  {tempPwd}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(tempPwd);
                    toast.success("Mot de passe copié.");
                  }}
                  data-testid="btn-copy-temp-password"
                  title="Copier"
                  className="p-2 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10"
                >
                  <Copy size={13} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setTempPwd("")}
                  title="Masquer"
                  className="p-2 border border-white/20 text-white/60 hover:bg-white/5"
                >
                  <X size={13} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <p className="text-sm text-white/45 italic">
                Chiffré (hash bcrypt) — non lisible. Régénérez un mot de passe temporaire ci-dessous.
              </p>
            )}
            <p className="text-[11px] text-white/45 mt-2 leading-snug">
              {tempPwd
                ? "⚠ Ce mot de passe n'apparaît qu'une seule fois. Transmettez-le au client via un canal sécurisé, puis fermez cette carte."
                : "Le mot de passe original n'est pas stocké en clair. Vous pouvez générer un mot de passe temporaire à communiquer au client."}
            </p>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[#DCB457]/15 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm("Générer un nouveau mot de passe temporaire ? Le client ne pourra plus se connecter avec son mot de passe actuel.")) return;
              setResetPwdBusy(true);
              try {
                const res = await adminApi.resetUserPassword(id);
                setTempPwd(res.password || "");
                toast.success("Nouveau mot de passe généré.");
              } catch (e) {
                toast.error(formatApiError(e));
              } finally {
                setResetPwdBusy(false);
              }
            }}
            disabled={resetPwdBusy}
            data-testid="btn-reset-password"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 disabled:opacity-60"
          >
            <Key size={12} strokeWidth={1.7} /> {resetPwdBusy ? "Génération…" : "Générer un mot de passe temporaire"}
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm("Réinitialiser la playlist de ce client ? Toutes les pistes et notes seront supprimées, la playlist repassera à \"à renseigner\".")) return;
              setResetPlaylistBusy(true);
              try {
                await adminApi.resetUserPlaylist(id);
                toast.success("Playlist réinitialisée.");
                load();
              } catch (e) {
                toast.error(formatApiError(e));
              } finally {
                setResetPlaylistBusy(false);
              }
            }}
            disabled={resetPlaylistBusy}
            data-testid="btn-reset-playlist"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-white/20 text-white/75 hover:bg-white/5 disabled:opacity-60"
          >
            <ListMusic size={12} strokeWidth={1.7} /> {resetPlaylistBusy ? "Réinitialisation…" : "Réinitialiser la playlist"}
          </button>
        </div>
      </Section>

      <Section title={`Demandes de devis (${data.commandes.length})`} testid="sec-commandes">
        {data.commandes.length === 0 ? <p className="text-sm text-white/45 italic">Aucune demande.</p> : (
          <ul className="space-y-3">
            {data.commandes.map((c) => (
              <li key={c.id} className="border border-[#DCB457]/15 p-4 text-sm">
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <span className="font-serif-luxe">{c.type_evenement}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/55">{c.status}</span>
                </div>
                <p className="text-white/55 text-xs mt-1">{c.date_evenement || c.created_at?.slice(0, 10)} · {c.lieu} · {c.nb_invites}</p>
                {c.message && <p className="text-white/65 mt-2 text-xs italic">"{c.message}"</p>}
                <div className="mt-3">
                  <button
                    onClick={async () => {
                      try {
                        const res = await adminApi.createBillingFromQuote(c.id);
                        toast.success(`Devis ${res.number} créé.`);
                        navigate(`/admin/devis/${res.id}`);
                      } catch (e) {
                        toast.error(formatApiError(e));
                      }
                    }}
                    data-testid={`btn-create-devis-${c.id}`}
                    className="text-[11px] inline-flex items-center gap-1.5 text-[#DCB457] border border-[#DCB457]/40 px-2.5 py-1 hover:bg-[#DCB457]/10"
                  >
                    <FilePlus size={11} strokeWidth={1.4} /> Créer un devis pré-rempli
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Playlist" testid="sec-playlist">
        {!data.playlist ? <NotFilled testid="playlist-empty" /> : (
          <div className="space-y-4 text-sm">
            <div className="space-y-2">
              {data.playlist.entrance_song && <KV k="Entrée" v={data.playlist.entrance_song} />}
              {data.playlist.first_dance && <KV k="Première danse" v={data.playlist.first_dance} />}
              {!!data.playlist.must_play?.length && <KV k="Incontournables" v={data.playlist.must_play.join(" · ")} />}
              {!!data.playlist.do_not_play?.length && <KV k="À ne pas passer" v={data.playlist.do_not_play.join(" · ")} />}
              {data.playlist.notes && <KV k="Notes" v={data.playlist.notes} />}
            </div>
            <SpotifyTracksByMoment tracks={data.playlist.spotify_tracks || []} />
          </div>
        )}
      </Section>

      <Section title="Questionnaire mariage" testid="sec-quest">
        {!data.questionnaire ? <NotFilled testid="quest-empty" /> : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(data.questionnaire).filter(([k]) => !["_id", "id", "user_id", "created_at", "updated_at"].includes(k)).map(([k, v]) => (
              <KV key={k} k={k.replace(/_/g, " ")} v={v} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Questionnaire événement (préparation)" testid="sec-quest-prep">
        {!data.questionnaire_prep ? <NotFilled testid="quest-prep-empty" /> : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(data.questionnaire_prep).filter(([k]) => !["_id", "id", "user_id", "created_at", "updated_at"].includes(k)).map(([k, v]) => (
              <KV key={k} k={k.replace(/_/g, " ")} v={typeof v === "object" ? JSON.stringify(v) : v} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Photobooth" testid="sec-pb">
        {!data.photobooth ? <NotFilled testid="pb-empty" /> : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(data.photobooth).filter(([k]) => !["_id", "id", "user_id", "created_at", "updated_at"].includes(k)).map(([k, v]) => (
              <KV key={k} k={k.replace(/_/g, " ")} v={String(v)} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Planning" testid="sec-planning">
        {!data.planning?.items?.length ? <NotFilled testid="planning-empty" /> : (
          <ul className="space-y-2 text-sm">
            {data.planning.items.map((it, i) => (
              <li key={i} className="flex gap-3 border-b border-[#DCB457]/10 pb-2">
                <span className="text-[#DCB457] w-16 font-mono">{it.heure}</span>
                <span className="flex-1">{it.label}</span>
                <span className="text-white/45 text-xs">{it.notes}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Rendez-vous" testid="sec-rdv">
        <form onSubmit={addRdv} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-5">
          <div className="md:col-span-2"><DateTimeField date={rdvForm.date.slice(0, 10)} time={rdvForm.date.slice(11, 16)} onChange={(d, t) => setRdvForm({ ...rdvForm, date: d ? `${d}T${t || "10:00"}` : "" })} testid="rdv-date" /></div>
          <input placeholder="Type" value={rdvForm.type} onChange={(e) => setRdvForm({ ...rdvForm, type: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none" data-testid="rdv-type" />
          <input placeholder="Lieu" value={rdvForm.lieu} onChange={(e) => setRdvForm({ ...rdvForm, lieu: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none" data-testid="rdv-lieu" />
          <input placeholder="Notes" value={rdvForm.notes} onChange={(e) => setRdvForm({ ...rdvForm, notes: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none" data-testid="rdv-notes" />
          <button type="submit" data-testid="rdv-add" className="btn-gold inline-flex items-center justify-center gap-1"><Plus size={14} strokeWidth={1.4} /> Ajouter</button>
        </form>
        {data.rdv.length === 0 ? <p className="text-sm text-white/45 italic">Aucun RDV planifié.</p> : (
          <ul className="space-y-2 text-sm">
            {data.rdv.map((r) => (
              <li key={r.id} className="flex items-center gap-3 border-b border-[#DCB457]/10 pb-2">
                <span className="text-[#DCB457] font-mono">{r.date ? `${r.date.slice(8, 10)}/${r.date.slice(5, 7)}/${r.date.slice(0, 4)}${r.date.length > 10 ? ` à ${r.date.slice(11, 16).replace(":", "h")}` : ""}` : ""}</span>
                <span className="flex-1">{r.type} {r.lieu && <span className="text-white/45">· {r.lieu}</span>}</span>
                <button onClick={() => delRdv(r.id)} className="text-red-300 hover:text-red-400"><Trash2 size={13} strokeWidth={1.4} /></button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Devis · Contrat · Assurance" testid="sec-contrat">
        <form onSubmit={saveContrat} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input placeholder="N° contrat" value={contratForm.numero} onChange={(e) => setContratForm({ ...contratForm, numero: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none" data-testid="ct-numero" />
          <input placeholder="Montant" value={contratForm.montant} onChange={(e) => setContratForm({ ...contratForm, montant: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none" data-testid="ct-montant" />
          <select value={contratForm.statut} onChange={(e) => setContratForm({ ...contratForm, statut: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none" data-testid="ct-statut">
            {["brouillon", "envoye", "signe", "paye"].map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
          </select>
          <DatePickerField value={contratForm.date_signature} onChange={(v) => setContratForm({ ...contratForm, date_signature: v })} placeholder="Date de signature" clearable testid="ct-date" />
          <input placeholder="URL PDF contrat" value={contratForm.url_pdf} onChange={(e) => setContratForm({ ...contratForm, url_pdf: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none md:col-span-2" data-testid="ct-pdf" />
          <input placeholder="URL attestation assurance" value={contratForm.assurance_url} onChange={(e) => setContratForm({ ...contratForm, assurance_url: e.target.value })} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none md:col-span-2" data-testid="ct-ins" />
          <textarea placeholder="Notes" value={contratForm.notes} onChange={(e) => setContratForm({ ...contratForm, notes: e.target.value })} rows={3} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none md:col-span-2" data-testid="ct-notes" />
          <button type="submit" data-testid="ct-save" className="btn-gold md:col-span-2 inline-flex items-center justify-center gap-2"><FileText size={14} strokeWidth={1.4} /> Enregistrer le contrat</button>
        </form>
      </Section>
    </div>
  );
}

const NotFilled = ({ testid }) => (
  <p className="text-sm text-white/45 italic" data-testid={testid}>Pas encore rempli par le client.</p>
);

const TRACK_MOMENTS = [
  ["entree", "Entrée des mariés"], ["vin_honneur", "Vin d'honneur"], ["cocktail", "Cocktail"],
  ["ouverture_bal", "Ouverture de bal"], ["premiere_danse", "Première danse"], ["dessert", "Dessert"],
  ["soiree", "Soirée"], ["must_play", "Incontournables"], ["first_dance", "Première danse"],
  ["entrance", "Entrée"], ["do_not_play", "À ne pas passer"],
];

function SpotifyTracksByMoment({ tracks }) {
  if (!tracks.length) return null;
  const known = TRACK_MOMENTS.map(([k]) => k);
  const groups = [...TRACK_MOMENTS, ["__other", "Autres"]]
    .map(([k, label]) => [k, label, tracks.filter((t) => (k === "__other" ? !known.includes(t.category) : t.category === k))])
    .filter(([, , list]) => list.length);
  return (
    <div className="space-y-4" data-testid="playlist-spotify-tracks">
      <p className="text-[10px] uppercase tracking-[0.25em] text-white/45">{tracks.length} morceau{tracks.length > 1 ? "x" : ""} Spotify</p>
      {groups.map(([k, label, list]) => (
        <div key={k} data-testid={`playlist-moment-${k}`}>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#DCB457] mb-2">{label} ({list.length})</p>
          <ul className="space-y-1.5">
            {list.map((t, i) => (
              <li key={`${t.id}-${i}`} className="flex items-center gap-3 border-b border-[#DCB457]/10 pb-1.5">
                {t.album_image && <img src={t.album_image} alt="" className="w-8 h-8 object-cover" loading="lazy" />}
                <span className="flex-1 min-w-0 truncate">{t.name} <span className="text-white/45">— {t.artists}</span></span>
                {t.note && <span className="text-white/45 text-xs italic truncate max-w-[40%]">{t.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const STATUS_ASSIGN_LABELS = { pressenti: "Pressenti", reserve: "Réservé", annule: "Annulé" };

// ────────────────────────────────────────────────────────────
// MyEvent NEXT section — fiche client unifiée (Sprint 2)
// Réutilise les endpoints existants /api/myadmin/clients/:userId
// et /api/myadmin/events/:eventId — AUCUNE MODIF BACKEND.
// ────────────────────────────────────────────────────────────
const MODULE_LABELS = {
  MYEVENT: "Event", MYPLANNING: "Planning", MYGUESTS: "Guests",
  MYPRO: "Pro", MYMEMORIES: "Memories", MYWALLET: "Wallet",
  MYMUSIC: "Music", MYASSISTANT: "Assistant", MYLIVE: "Live",
};

function MyEventSection({ userId }) {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [payload, setPayload] = React.useState(null);
  const [eventsDetail, setEventsDetail] = React.useState({}); // { event_id: { subscription, plan } }
  const [plansById, setPlansById] = React.useState({}); // { plan_id: {..., plan_features:[...]} }

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [baseRes, plansRes] = await Promise.all([
          api.get(`/myadmin/clients/${userId}`),
          api.get(`/myadmin/plans`),
        ]);
        if (cancelled) return;
        setPayload(baseRes.data);
        const byId = {};
        (plansRes.data.plans || []).forEach((p) => { byId[p.id] = p; });
        setPlansById(byId);
        // For each event, fetch subscription + plan (parallel)
        const events = baseRes.data.events || [];
        if (events.length > 0) {
          const detailPromises = events.map((e) =>
            api.get(`/myadmin/events/${e.id}`).then((r) => [e.id, r.data]).catch(() => [e.id, null])
          );
          const results = await Promise.all(detailPromises);
          if (cancelled) return;
          const map = {};
          results.forEach(([eid, val]) => { if (val) map[eid] = val; });
          setEventsDetail(map);
        }
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <Section title="Espace MyEvent NEXT" testid="sec-myevent">
      {loading && <p className="text-white/45 text-sm italic">Chargement…</p>}
      {err && <p className="text-red-300 text-sm">{err}</p>}
      {!loading && !err && payload && (payload.events || []).length === 0 && (
        <p className="text-white/50 text-sm italic" data-testid="myevent-empty">
          Aucun événement MyEvent NEXT pour ce client — dossier Legacy uniquement.
        </p>
      )}
      {!loading && !err && payload && (payload.events || []).length > 0 && (
        <div className="space-y-4">
          {payload.events.map((e) => {
            const detail = eventsDetail[e.id] || {};
            const sub = detail.subscription;
            const plan = detail.plan;
            // Fetch plan_features from the plans list (joined locally to avoid N+1)
            const features = (plan && plansById[plan.id]?.plan_features) || [];
            const enabledCount = features.filter((f) => f.enabled).length;
            return (
              <div
                key={e.id}
                data-testid={`myevent-card-${e.id}`}
                className="border border-[#DCB457]/20 hover:border-[#DCB457]/40 transition-colors p-4"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Calendar size={14} strokeWidth={1.4} className="text-[#DCB457] shrink-0" />
                      <Link
                        to={`/admin/events/${e.id}`}
                        data-testid={`myevent-link-${e.id}`}
                        className="font-serif-luxe text-lg text-white hover:text-[#DCB457]"
                      >
                        {e.name || "Événement sans nom"}
                      </Link>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457]/85 border border-[#DCB457]/30 px-1.5 py-0.5">
                        {e.status || "—"}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/50 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
                      <span>ID {e.id.slice(0, 8)}…</span>
                      {e.date && <span>Date {fmtDate(e.date)}</span>}
                      {e.location && <span>Lieu {e.location}</span>}
                    </div>
                  </div>
                  <Link
                    to={`/admin/events/${e.id}`}
                    className="text-[11px] inline-flex items-center gap-1 text-[#DCB457] border border-[#DCB457]/40 px-2.5 py-1 hover:bg-[#DCB457]/10"
                    data-testid={`myevent-open-${e.id}`}
                  >
                    <Eye size={11} strokeWidth={1.5} /> Ouvrir
                  </Link>
                </div>

                {/* Subscription + plan */}
                <div className="mt-3 pt-3 border-t border-[#DCB457]/12 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Forfait</p>
                    <p className="text-white/85" data-testid={`myevent-plan-${e.id}`}>
                      {plan ? plan.name : <span className="text-white/30 italic">Aucun</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Abonnement</p>
                    <p data-testid={`myevent-sub-status-${e.id}`}>
                      {sub ? (
                        <span className={`text-xs uppercase tracking-[0.16em] px-1.5 py-0.5 border ${
                          sub.status === "active"
                            ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/10"
                            : "text-amber-200 border-amber-400/30"
                        }`}>
                          {sub.status}
                        </span>
                      ) : (
                        <span className="text-white/30 italic text-xs">Non souscrit</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Modules activés</p>
                    <p className="text-white/85" data-testid={`myevent-modules-count-${e.id}`}>
                      {features.length > 0
                        ? <><Sparkles size={11} strokeWidth={1.5} className="inline text-[#DCB457] mr-1" />{enabledCount} / {features.length}</>
                        : <span className="text-white/30 italic">—</span>}
                    </p>
                  </div>
                </div>

                {/* Feature list */}
                {features.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`myevent-features-${e.id}`}>
                    {features.map((f) => (
                      <span
                        key={f.feature_key}
                        title={`${f.feature_key} · tier=${f.tier}`}
                        className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border ${
                          f.enabled
                            ? "text-[#DCB457] border-[#DCB457]/50"
                            : "text-white/25 border-white/15 line-through"
                        }`}
                      >
                        {MODULE_LABELS[f.feature_key] || f.feature_key}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {!loading && !err && payload && (payload.memberships || []).length > 0 && (payload.events || []).length === 0 && (
        <p className="text-xs text-amber-200 mt-3 italic">
          ⚠ Le client a {(payload.memberships || []).length} membership(s) event mais aucun event résolu — vérifier la cohérence.
        </p>
      )}
    </Section>
  );
}


function PrestatairesSection({ userId, assigned, onChange }) {
  const [directory, setDirectory] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState("");
  const [selectedStatus, setSelectedStatus] = React.useState("pressenti");
  const [notes, setNotes] = React.useState("");
  const [totalAmount, setTotalAmount] = React.useState("");
  const [depositReq, setDepositReq] = React.useState("");
  const [depositPaid, setDepositPaid] = React.useState("");
  const [dueDate, setDueDate] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [editingAssign, setEditingAssign] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});

  React.useEffect(() => {
    adminApi.listPrestataires().then(setDirectory).catch(() => setDirectory([]));
  }, []);

  const availableForAdd = directory.filter((p) => !assigned.some((a) => a.prestataire_id === p.id));

  const doAssign = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    try {
      const payload = {
        prestataire_id: selectedId,
        status: selectedStatus,
        notes,
        total_amount: totalAmount ? Number(totalAmount) : null,
        deposit_required: depositReq ? Number(depositReq) : null,
        deposit_paid: depositPaid ? Number(depositPaid) : null,
        invoice_due_date: dueDate || null,
      };
      await adminApi.assignPrestataire(userId, payload);
      toast.success("Prestataire assigné au dossier.");
      setShowAdd(false);
      setSelectedId(""); setSelectedStatus("pressenti"); setNotes("");
      setTotalAmount(""); setDepositReq(""); setDepositPaid(""); setDueDate("");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const setStatus = async (a, next) => {
    try {
      await adminApi.updateAssignment(a.id, { status: next });
      toast.success(next === "reserve" ? "Prestataire réservé." : "Statut mis à jour.");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Retirer ${a.prestataire?.name || "ce prestataire"} du dossier ?`)) return;
    try { await adminApi.removeAssignment(a.id); toast.success("Retiré du dossier."); onChange(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const openEditAssign = (a) => {
    setEditForm({
      total_amount: a.total_amount ?? "",
      deposit_required: a.deposit_required ?? "",
      deposit_paid: a.deposit_paid ?? "",
      invoice_due_date: a.invoice_due_date || "",
      notes: a.notes || "",
    });
    setEditingAssign(a);
  };
  const saveEditAssign = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        total_amount: editForm.total_amount === "" ? null : Number(editForm.total_amount),
        deposit_required: editForm.deposit_required === "" ? null : Number(editForm.deposit_required),
        deposit_paid: editForm.deposit_paid === "" ? null : Number(editForm.deposit_paid),
        invoice_due_date: editForm.invoice_due_date || null,
        notes: editForm.notes || "",
      };
      await adminApi.updateAssignment(editingAssign.id, payload);
      toast.success("Facturation mise à jour.");
      setEditingAssign(null);
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Section
      title="Prestataires du dossier"
      testid="sec-prestataires"
      action={
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          disabled={availableForAdd.length === 0}
          data-testid="btn-add-prestataire"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 disabled:opacity-40"
          title={availableForAdd.length === 0 ? "Tous les prestataires du répertoire sont déjà assignés" : ""}
        >
          <Plus size={12} strokeWidth={1.5} /> Ajouter
        </button>
      }
    >
      {directory.length === 0 && (
        <p className="text-xs text-white/50 italic mb-3">
          Votre répertoire est vide. <Link to="/admin/prestataires" className="text-[#DCB457] hover:underline">Créez vos premiers prestataires</Link> puis assignez-les à ce dossier.
        </p>
      )}
      {assigned.length > 0 ? (
        <div className="space-y-2">
          {assigned.map((a) => {
            const p = a.prestataire || {};
            const isReserved = a.status === "reserve";
            const isCancelled = a.status === "annule";
            return (
              <div
                key={a.id}
                data-testid={`assigned-${a.id}`}
                className={`flex items-start gap-3 border px-3 py-2.5 transition-colors ${
                  isReserved ? "border-emerald-400/40 bg-emerald-500/5" :
                  isCancelled ? "border-white/10 opacity-50" :
                  "border-[#DCB457]/25"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-white/90 font-medium">{p.name || "?"}</span>
                    {p.specialty && <span className="text-xs text-white/50">· {p.specialty}</span>}
                    <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 border ${
                      isReserved ? "border-emerald-400/40 text-emerald-300 bg-emerald-500/10" :
                      isCancelled ? "border-white/15 text-white/45" :
                      "border-amber-400/40 text-amber-200 bg-amber-500/10"
                    }`}>
                      {STATUS_ASSIGN_LABELS[a.status] || a.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/55 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {p.phone && <span>📞 {p.phone}</span>}
                    {p.email && <span>{p.email}</span>}
                    {p.tariff_indicative != null && <span>Tarif indic. {Number(p.tariff_indicative).toLocaleString("fr-FR")} €</span>}
                    <span>Assigné {fmtDate(a.assigned_at)}</span>
                    {a.booked_at && <span className="text-emerald-300">Réservé {fmtDate(a.booked_at)}</span>}
                  </div>
                  {(a.total_amount != null || a.deposit_required != null || a.invoice_due_date) && (() => {
                    const paid = Number(a.deposit_paid || 0);
                    const req = Number(a.deposit_required || 0);
                    const total = Number(a.total_amount || 0);
                    const balance = Math.max(0, total - paid);
                    return (
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] border-t border-[#DCB457]/10 pt-2">
                        {a.total_amount != null && <span className="text-white/70">Total : <strong className="text-[#DCB457]">{Number(a.total_amount).toLocaleString("fr-FR")} €</strong></span>}
                        {a.deposit_required != null && <span className="text-white/70">Acompte à verser : <strong>{Number(a.deposit_required).toLocaleString("fr-FR")} €</strong></span>}
                        {a.deposit_paid != null && <span className={paid >= req && req > 0 ? "text-emerald-300" : "text-white/70"}>Acompte payé : <strong>{paid.toLocaleString("fr-FR")} €</strong></span>}
                        {total > 0 && <span className="text-white/70">Solde : <strong className={balance === 0 ? "text-emerald-300" : "text-amber-300"}>{balance.toLocaleString("fr-FR")} €</strong></span>}
                        {a.invoice_due_date && <span className="text-white/60">Échéance : <strong>{fmtDate(a.invoice_due_date)}</strong></span>}
                      </div>
                    );
                  })()}
                  {a.notes && <p className="text-[11px] text-white/60 mt-1 italic">« {a.notes} »</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEditAssign(a)}
                    title="Modifier la facturation"
                    data-testid={`edit-billing-${a.id}`}
                    className="inline-flex items-center text-white/70 hover:bg-white/5 px-1.5 py-1.5 border border-white/15 text-[11px]"
                  >
                    <Pencil size={11} strokeWidth={1.4} />
                  </button>
                  {!isReserved && !isCancelled && (
                    <button
                      onClick={() => setStatus(a, "reserve")}
                      title="Marquer comme réservé"
                      data-testid={`reserve-${a.id}`}
                      className="inline-flex items-center gap-1 text-emerald-300 hover:bg-emerald-500/10 px-2 py-1.5 border border-emerald-400/40 text-[11px]"
                    >
                      <CheckCircle2 size={11} strokeWidth={1.5} /> Réserver
                    </button>
                  )}
                  {isReserved && (
                    <button
                      onClick={() => setStatus(a, "pressenti")}
                      title="Repasser en pressenti"
                      className="inline-flex items-center gap-1 text-amber-300 hover:bg-amber-500/10 px-2 py-1.5 border border-amber-400/30 text-[11px]"
                    >
                      Pressenti
                    </button>
                  )}
                  <button
                    onClick={() => remove(a)}
                    title="Retirer du dossier"
                    data-testid={`remove-${a.id}`}
                    className="inline-flex items-center text-red-300 hover:bg-red-500/10 px-1.5 py-1.5 border border-red-400/30 text-[11px]"
                  >
                    <X size={11} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-white/50 text-sm italic">Aucun prestataire assigné à ce dossier.</p>
      )}

      {showAdd && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => !saving && setShowAdd(false)}>
          <form onSubmit={doAssign} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0a0a0a] border border-[#DCB457]/30 p-6" data-testid="assign-modal">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Assigner un prestataire</p>
              <button type="button" onClick={() => setShowAdd(false)} disabled={saving} className="text-white/50 hover:text-white"><X size={14} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Prestataire</label>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  data-testid="select-prestataire"
                  className="input-luxe mt-1 w-full bg-black/60"
                  required
                >
                  <option value="" className="bg-black">— Choisir —</option>
                  {availableForAdd.map((p) => (
                    <option key={p.id} value={p.id} className="bg-black">
                      {p.name}{p.specialty ? ` · ${p.specialty}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Statut initial</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="input-luxe mt-1 w-full bg-black/60"
                >
                  <option value="pressenti" className="bg-black">Pressenti (à confirmer)</option>
                  <option value="reserve" className="bg-black">Réservé (finalisé)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Notes (facultatif)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="input-luxe mt-1 w-full min-h-[60px]" placeholder="Ex : dispo le 15/06, à recontacter" />
              </div>
              <div className="border-t border-white/10 pt-3 mt-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">Facturation (facultatif)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-white/50">Total (€)</label>
                    <input type="number" step="0.01" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} className="input-luxe mt-0.5 w-full" placeholder="700" />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/50">Acompte à verser (€)</label>
                    <input type="number" step="0.01" value={depositReq} onChange={(e) => setDepositReq(e.target.value)} className="input-luxe mt-0.5 w-full" placeholder="210" />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/50">Acompte payé (€)</label>
                    <input type="number" step="0.01" value={depositPaid} onChange={(e) => setDepositPaid(e.target.value)} className="input-luxe mt-0.5 w-full" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/50">Échéance</label>
                    <DatePickerField value={dueDate} onChange={setDueDate} clearable className="mt-0.5" testid="invoice-due-date" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="submit" disabled={saving || !selectedId} data-testid="submit-assign" className="btn-gold flex-1 py-2 text-sm disabled:opacity-60">
                {saving ? "Enregistrement…" : "Assigner"}
              </button>
              <button type="button" onClick={() => setShowAdd(false)} disabled={saving} className="border border-white/20 text-white/65 hover:bg-white/5 px-4 py-2 text-sm">Annuler</button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {editingAssign && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => !saving && setEditingAssign(null)}>
          <form onSubmit={saveEditAssign} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0a0a0a] border border-[#DCB457]/30 p-6" data-testid="edit-billing-modal">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Facturation — {editingAssign.prestataire?.stage_name || editingAssign.prestataire?.name}</p>
              <button type="button" onClick={() => setEditingAssign(null)} disabled={saving} className="text-white/50 hover:text-white"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Total (€)</label>
                <input type="number" step="0.01" value={editForm.total_amount} onChange={(e) => setEditForm({...editForm, total_amount: e.target.value})} className="input-luxe mt-1 w-full" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Acompte à verser (€)</label>
                <input type="number" step="0.01" value={editForm.deposit_required} onChange={(e) => setEditForm({...editForm, deposit_required: e.target.value})} className="input-luxe mt-1 w-full" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Acompte payé (€)</label>
                <input type="number" step="0.01" value={editForm.deposit_paid} onChange={(e) => setEditForm({...editForm, deposit_paid: e.target.value})} className="input-luxe mt-1 w-full" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Date d'échéance</label>
                <DatePickerField value={editForm.invoice_due_date} onChange={(v) => setEditForm({ ...editForm, invoice_due_date: v })} clearable className="mt-1" testid="edit-invoice-due-date" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Notes</label>
                <textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} className="input-luxe mt-1 w-full min-h-[60px]" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="submit" disabled={saving} className="btn-gold flex-1 py-2 text-sm disabled:opacity-60">{saving ? "…" : "Enregistrer"}</button>
              <button type="button" onClick={() => setEditingAssign(null)} disabled={saving} className="border border-white/20 text-white/65 hover:bg-white/5 px-4 py-2 text-sm">Annuler</button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </Section>
  );
}

